import type * as ChatSdkModule from "@cometchat/chat-sdk-javascript";

// The app-wide chat connection (unread badge, presence) runs on the bare SDK. The UI kit and
// the calls SDK are several MB and only the /chats page needs them: it loads them and registers
// here (uikit_session.ts), after which auth calls go through the kit exactly as they used to.
// The SDK itself is loaded on demand too: a browser with no chat session never needs it.

type Sdk = typeof ChatSdkModule.CometChat;
type ChatUser = InstanceType<typeof ChatSdkModule.CometChat.User>;

export interface ChatAuthKit {
	getLoggedinUser: () => Promise<ChatUser | null>;
	loginWithAuthToken: (authToken: string) => Promise<ChatUser>;
	logout: () => Promise<unknown>;
}

const APP_ID = import.meta.env.VITE_COMETCHAT_APP_ID;
const REGION = import.meta.env.VITE_COMETCHAT_REGION;
// "1" once a logout went through and nothing logged in since: no chat session to clean up here.
const SIGNED_OUT_KEY = "cc_signed_out_v1";

let sdk: Sdk | null = null;
let sdkLoading: Promise<Sdk> | null = null;
let kit: ChatAuthKit | null = null;
let applyKitLanguage: ((language: string | undefined) => void) | null = null;
let language: string | undefined;

function loadChatSdk(): Promise<Sdk> {
	sdkLoading ??= import("@cometchat/chat-sdk-javascript").then(module => {
		sdk = module.CometChat;
		return sdk;
	});
	sdkLoading.catch(() => {
		sdkLoading = null;
	});
	return sdkLoading;
}

// For code that runs only after initChat() has resolved (a logged-in chat user).
export function chatSdk(): Sdk {
	if (!sdk) throw new Error("Chat SDK is not loaded yet");
	return sdk;
}

export function isChatSignedOut(): boolean {
	try {
		return localStorage.getItem(SIGNED_OUT_KEY) === "1";
	} catch {
		return false;
	}
}

function setChatSignedOut(signedOut: boolean) {
	try {
		if (signedOut) localStorage.setItem(SIGNED_OUT_KEY, "1");
		else localStorage.removeItem(SIGNED_OUT_KEY);
	} catch {
		// storage unavailable: the SDK then simply loads on every visit
	}
}

// Same settings the UI kit builds from UIKitSettingsBuilder in uikit_session.ts.
export async function initChat(): Promise<unknown> {
	const CometChat = await loadChatSdk();
	const appSettings = new CometChat.AppSettingsBuilder()
		.subscribePresenceForAllUsers()
		.setRegion(REGION)
		.autoEstablishSocketConnection(true)
		.setStorageMode(CometChat.StorageMode.LOCAL)
		.build();
	return CometChat.init(APP_ID, appSettings);
}

export async function getLoggedInChatUser(): Promise<ChatUser | null> {
	return kit ? kit.getLoggedinUser() : (await loadChatSdk()).getLoggedinUser();
}

export async function loginToChat(authToken: string): Promise<ChatUser> {
	setChatSignedOut(false);
	return kit ? kit.loginWithAuthToken(authToken) : (await loadChatSdk()).login(authToken);
}

export async function logoutOfChat(): Promise<unknown> {
	const CometChat = await loadChatSdk();
	try {
		return await (kit ? kit.logout() : CometChat.logout());
	} finally {
		// The SDK clears its stored session before settling, even when the server call fails.
		setChatSignedOut(true);
	}
}

export function registerChatKit(
	authKit: ChatAuthKit,
	setKitLanguage: (language: string | undefined) => void,
) {
	kit = authKit;
	applyKitLanguage = setKitLanguage;
	setKitLanguage(language);
}

// Called on every interface language change; reaches the kit once it is loaded.
export function setChatLanguage(next: string | undefined) {
	language = next;
	applyKitLanguage?.(next);
}
